import {
  Directive,
  ElementRef,
  inject,
  InputSignal,
  OnDestroy,
  input,
  effect,
  untracked,
  output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { createTooltip } from './tooltip';

type TYPosition = 'start' | 'center-start' | 'center' | 'center-end' | 'end';
type TXPosition = 'start' | 'center' | 'end';

@Directive({
  selector: '[lib-tooltip]',
  standalone: true,
  exportAs: 'libTooltip',
})
export class TooltipDirective implements OnDestroy {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly translateService = inject(TranslateService);
  private tooltip: HTMLElement | null = null;
  private isTooltipVisible: boolean = false;

  public tooltipTitle: InputSignal<string> = input<string>();
  public tooltipContent: InputSignal<string> = input<string>();
  public tooltipMultiples: InputSignal<{ title?: string; content?: string; }[]> = input<{ title?: string; content?: string }[]>();
  public hasDistance: InputSignal<boolean> = input<boolean>(true);
  public disableTooltip: InputSignal<boolean> = input<boolean>(false);
  public readonly openOnClick: InputSignal<boolean> = input<boolean>(false);
  public removedTooltip = output<void>();

  public xAxis: InputSignal<TXPosition> = input<TXPosition>('start');
  public yAxis: InputSignal<TYPosition> = input<TYPosition>('end');

  public ngOnDestroy(): void {
    this.removeTooltip();
    this.removeListeners();
  }

  private removeListeners(): void {
    this.elementRef.nativeElement.removeEventListener('mouseenter', this.createTooltip);
    this.elementRef.nativeElement.removeEventListener('click', this.handleClick);
    this.elementRef.nativeElement.removeEventListener('mouseleave', this.removeTooltip);
    document.body.removeEventListener('wheel', this.removeTooltip);
    document.body.removeEventListener('touchmove', this.removeTooltip);
  }

  private readonly observeToCreateTooltip = effect(() => {
    const title = this.tooltipTitle();
    const content = this.tooltipContent();
    const multiples = this.tooltipMultiples();
    const disableTooltip = this.disableTooltip();
    const openOnClick = this.openOnClick();
    
    untracked(() => {
      this.removeListeners();
      if ((title || content || multiples?.length) && !disableTooltip) {
        if (openOnClick) {
          this.elementRef.nativeElement.addEventListener('click', this.handleClick, { passive: true });
        } else {
          this.elementRef.nativeElement.addEventListener('mouseenter', this.createTooltip, { passive: true });
        }
        this.elementRef.nativeElement.addEventListener('mouseleave', this.removeTooltip, { passive: true });
        document.body.addEventListener('wheel', this.removeTooltip, { passive: true });
        document.body.addEventListener('touchmove', this.removeTooltip, { passive: true });
        if (this.tooltip && this.isTooltipVisible) {
          this.tooltip?.remove();
          this.tooltip = null;
          this.isTooltipVisible = false;
          this.createTooltip();
        }
      }
    });
  });

  private readonly handleClick = (): void => {
    if (this.disableTooltip()) return;
    if (this.isTooltipVisible) return;
    this.createTooltip();
  };

  public readonly removeTooltip = (): void => {
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = null;
      this.isTooltipVisible = false;
      this.removedTooltip.emit();
    }
  };

  private readonly createTooltip = (): void => {
    if (this.disableTooltip() || this.isTooltipVisible) return;
    
    this.tooltip = createTooltip({
      content: this.tooltipContent() ? this.translateService.instant(this.tooltipContent()) : null,
      title: this.tooltipTitle() ? this.translateService.instant(this.tooltipTitle()) : null,
      multiples: this.tooltipMultiples()?.map(item => ({
            title: item.title ? this.translateService.instant(item.title) : null,
            content: item.content ? this.translateService.instant(item.content) : null,
          })) || null,
      element: this.elementRef.nativeElement,
      hasDistance: this.hasDistance(),
      xAxis: this.xAxis(),
      yAxis: this.yAxis(),
    });

    this.isTooltipVisible = true;
    if (this.openOnClick()) {
      this.tooltip.classList.add('animate-shaking');
    }
  };
}